/**
 * D08 consistency contract: the ONE date/number formatter for user-facing
 * surfaces. Locale-aware (Bengali digits and month names on /bn), invalid-safe,
 * and timezone-capable. Do not add new local `formatDate` helpers; import from
 * here. Display defaults to the viewer's timezone; pass `timeZone` to pin
 * (Asia/Dhaka pinning is an open founder decision, see docs/audits
 * 2026-08-27 inventory, flag F2).
 */

const resolveLocale = (locale: string): string => (locale === "bn" ? "bn-BD" : "en-US");

interface FormatOptions {
  timeZone?: string;
  /** Returned when the value is missing or unparseable. */
  fallback?: string;
}

function toValidDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "Mar 3, 2026" / "৩ মার্চ, ২০২৬" */
export function formatDate(
  value: string | number | Date | null | undefined,
  locale: string,
  options: FormatOptions = {}
): string {
  const date = toValidDate(value);
  if (!date) return options.fallback ?? "";
  return new Intl.DateTimeFormat(resolveLocale(locale), {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: options.timeZone,
  }).format(date);
}

/** "Mar 3, 2026, 2:15 PM" with locale digits. */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  locale: string,
  options: FormatOptions = {}
): string {
  const date = toValidDate(value);
  if (!date) return options.fallback ?? "";
  return new Intl.DateTimeFormat(resolveLocale(locale), {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: options.timeZone,
  }).format(date);
}

/** "Oct 24, 8:14 PM" with locale digits: a day and a time, no year (`EX-SEC02`'s watermark). */
export function formatDayTime(
  value: string | number | Date | null | undefined,
  locale: string,
  options: FormatOptions = {}
): string {
  const date = toValidDate(value);
  if (!date) return options.fallback ?? "";
  return new Intl.DateTimeFormat(resolveLocale(locale), {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: options.timeZone,
  }).format(date);
}

/** "2:15 PM" with locale digits. */
export function formatTimeOfDay(
  value: string | number | Date | null | undefined,
  locale: string,
  options: FormatOptions = {}
): string {
  const date = toValidDate(value);
  if (!date) return options.fallback ?? "";
  return new Intl.DateTimeFormat(resolveLocale(locale), {
    hour: "numeric",
    minute: "2-digit",
    second: undefined,
    timeZone: options.timeZone,
  }).format(date);
}

/** Locale-digit number (Bengali numerals on /bn). */
export function formatNumber(
  value: number | null | undefined,
  locale: string,
  options: Intl.NumberFormatOptions = {}
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  return new Intl.NumberFormat(resolveLocale(locale), options).format(value);
}

/** File size with locale digits: "1.2 MB" / "১.২ MB". Unit stays Latin per docs/11. */
export function formatBytes(bytes: number | null | undefined, locale: string): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes) || bytes < 0) return "";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${formatNumber(Math.round(mb * 10) / 10, locale)} MB`;
  const kb = bytes / 1024;
  return `${formatNumber(Math.max(1, Math.round(kb)), locale)} KB`;
}

/** mm:ss countdown with locale digits (exam timers). */
export function formatClock(totalSeconds: number, locale: string): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const two = (n: number) => formatNumber(n, locale, { minimumIntegerDigits: 2 });
  return hours > 0
    ? `${formatNumber(hours, locale)}:${two(minutes)}:${two(seconds)}`
    : `${formatNumber(minutes, locale)}:${two(seconds)}`;
}

export { formatRelativeTime } from "./format-relative-time";
